import { Skeleton } from "../../../../core/Skeleton.js";
import { useImageFallback } from "../../../../foundations/useImageFallback.js";
import { BannerCarousel } from "../../../../patterns/BannerCarousel.js";
import { Hero } from "../../../../patterns/Hero.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import { useChromeHints } from "../../../shared/chromeHints.js";
import type { TemplateAction } from "../../../shared/content.js";
import type { BannersContent, HeroContent } from "../../homeContent.js";

/** A content action in the hero: the primary one is the page's brand button. */
function heroAction(action: TemplateAction) {
  return (
    <ActionButton
      key={`${action.href}${action.label}`}
      action={action}
      variant={action.emphasis === "primary" ? "brand" : "glass"}
      size="lg"
    />
  );
}

/**
 * The top of Home. While the store has banners live (or loading), every variant shows them as a
 * carousel. Otherwise its own headline, body and actions: the `split` variant sets its art, drawn
 * or by URL, beside the words on the stage, `centered` beneath them, and `carousel` draws them as
 * the carousel's one slide, so the top of Home is the same frame with banners live or not.
 * `shine` sweeps a light across a carousel's calls to action as the pointer arrives.
 */
export function HomeHero({
  variant = "split",
  shine = false,
  banners,
  ...hero
}: HeroContent & { variant?: "split" | "centered" | "carousel"; shine?: boolean }) {
  if (banners) return <HeroBanners banners={banners} shine={shine} />;
  if (variant === "carousel") return <HeroSlide {...hero} shine={shine} />;
  return <HeroStage {...hero} align={variant} />;
}

/**
 * The store's banners: the carousel, its visually hidden h1 the store's name, or while they load
 * the frame of a slide of art, which a slide with words may grow past.
 */
function HeroBanners({ banners, shine }: { banners: BannersContent; shine: boolean }) {
  if (banners.status === "loading") {
    return (
      <div aria-hidden className="pb-8 md:pb-12">
        <Skeleton className="aspect-[2/1] w-full rounded-none md:aspect-[9/2] md:max-h-[22rem]" />
        <div className="h-7" />
      </div>
    );
  }
  return (
    <div className="pb-8 md:pb-12">
      <h1 className="sr-only">{banners.heading}</h1>
      <BannerCarousel
        label={banners.label}
        slides={banners.slides.map((slide) => ({
          id: slide.id,
          title: slide.title,
          subtitle: slide.subtitle,
          art: slide.art && {
            wideSrc: slide.art.wide,
            phoneSrc: slide.art.phone,
            alt: slide.art.alt,
          },
          action: slide.action && (
            <ActionButton action={slide.action} variant="brand" size="lg" shine={shine} />
          ),
        }))}
      />
    </div>
  );
}

/** The hero on the stage, under the floating nav. */
function HeroStage({
  align,
  ...hero
}: Omit<HeroContent, "banners"> & { align: "split" | "centered" }) {
  // Its stage runs under a floating nav; Home's banners, shown in its place, don't.
  useChromeHints({ underNav: true });
  // Art by URL that is missing or fails to load is no media: the stage takes its layout without.
  const byUrl = useImageFallback(hero.artUrl);
  const art =
    hero.media ??
    (byUrl.failed ? undefined : (
      <img
        src={hero.artUrl}
        alt=""
        onError={byUrl.onError}
        className="max-h-[420px] w-auto rounded-2xl object-contain"
      />
    ));
  return (
    <Hero
      align={align}
      eyebrow={hero.eyebrow}
      title={hero.headline}
      body={hero.body}
      proof={hero.proof}
      media={art}
      actions={hero.actions.length > 0 ? hero.actions.map(heroAction) : undefined}
    />
  );
}

/**
 * The hero as the carousel's one slide: its headline (the page's h1), body and actions, over its
 * art by URL when it has some. A slide has no eyebrow, proof points or drawn art, so the hero's
 * `eyebrow`, `proof` and `media` are left to the stage.
 */
function HeroSlide({ shine, ...hero }: Omit<HeroContent, "banners"> & { shine: boolean }) {
  return (
    <div className="pb-8 md:pb-12">
      <BannerCarousel
        label={hero.headline}
        slides={[
          {
            id: "hero",
            title: hero.headline,
            titleAs: "h1",
            subtitle: hero.body,
            art: hero.artUrl ? { wideSrc: hero.artUrl, phoneSrc: hero.artUrl, alt: "" } : undefined,
            action:
              hero.actions.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {hero.actions.map((action) => (
                    <ActionButton
                      key={`${action.href}${action.label}`}
                      action={action}
                      variant={action.emphasis === "primary" ? "brand" : "secondary"}
                      size="lg"
                      shine={shine && action.emphasis === "primary"}
                    />
                  ))}
                </div>
              ) : undefined,
          },
        ]}
      />
    </div>
  );
}
